/**
 * Auto-check definitions for the AI track's spreadsheet exercises.
 *
 * These live here, apart from the module .jsx files, for one reason: the tests
 * must exercise the checks the trainees actually get. When a test declares its
 * own copy, the copy and the module drift, the test stays green, and the first
 * person to find out is a trainee being failed for something they did right.
 *
 * Everything is derived from apDataset.js, which is now a SPECIFICATION rather
 * than a fixture — the trainee's register is generated by a script they asked
 * for, so no two are alike. That rules out asserting values. What is left is
 * better anyway: does the sheet have the shape that was asked for, and does it
 * agree with itself.
 */

// Explicit .js extensions: the test suite imports this file in plain node,
// which (unlike Vite) will not guess them.
import {
  exactRow, rowCount, cellEquals, hasTabs, columnDescending, norm, toNumber,
  columnAllNumeric, columnValuesFrom, columnUnique, summaryMatchesSource,
} from '../lib/sheetsCheck.js'
import {
  AP_HEADERS, AP_TAB_NAME, ROW_COUNT, STATUSES, APPROVERS, AMOUNT_RANGE,
  SUMMARY_TAB_NAME, SUMMARY_HEADERS, BAND_COLUMN_HEADER, bandFor, money,
  FIRST_ASK_TAB, FIRST_ASK_VALUE,
} from './apDataset.js'

/**
 * Module 19 — Your First Ask.
 *
 * One tab, one cell. The exercise is not the spreadsheet, it is watching an
 * agent read your link, ask permission, and change a real file. So the check
 * is the smallest thing that proves it happened, and the trainee should be
 * able to pass it within a minute of a cold start.
 */
export const MODULE_19_CHECKS = [
  {
    label: `The spreadsheet has a tab called "${FIRST_ASK_TAB}"`,
    sheets: true,
    assert: hasTabs([FIRST_ASK_TAB]),
  },
  {
    label: `Cell A1 says "${FIRST_ASK_VALUE}"`,
    range: `${FIRST_ASK_TAB}!A1:A1`,
    assert: cellEquals(FIRST_ASK_VALUE),
  },
]

/**
 * Module 25 — Ask for the Data.
 *
 * The trainee describes a register and Claude writes a script that generates
 * it, so every sheet is different and not one amount can be asserted. These
 * check the shape that was asked for: the columns, the row count, values that
 * are the kind of thing they claim to be, and identifiers that identify.
 *
 * Column mapping: A Invoice, B Vendor, C Date, D Amount, E Status, F Approver.
 * The Date column is never compared — Sheets returns dates formatted to the
 * viewer's locale, so an AU trainee and a US one would disagree while both
 * being right.
 */
export const MODULE_25_CHECKS = [
  {
    label: `The spreadsheet has a tab called "${AP_TAB_NAME}"`,
    sheets: true,
    assert: hasTabs([AP_TAB_NAME]),
  },
  {
    label: 'Row 1 holds the six column headings, in order',
    range: `${AP_TAB_NAME}!A1:F1`,
    assert: exactRow(AP_HEADERS),
  },
  {
    label: `The script produced ${ROW_COUNT} invoices`,
    range: `${AP_TAB_NAME}!A2:A200`,
    assert: rowCount(ROW_COUNT),
  },
  {
    label: 'Every invoice number is different',
    range: `${AP_TAB_NAME}!A2:A200`,
    assert: columnUnique(),
  },
  {
    label: `Every amount is a number between $${money(AMOUNT_RANGE.min)} and $${money(AMOUNT_RANGE.max)}`,
    range: `${AP_TAB_NAME}!D2:D200`,
    assert: columnAllNumeric({ min: AMOUNT_RANGE.min, max: AMOUNT_RANGE.max }),
  },
  {
    label: `Every status is one of ${STATUSES.join(', ')}`,
    range: `${AP_TAB_NAME}!E2:E200`,
    assert: columnValuesFrom(STATUSES),
  },
  {
    label: `Every approver is one of ${APPROVERS.join(' or ')}`,
    range: `${AP_TAB_NAME}!F2:F200`,
    assert: columnValuesFrom(APPROVERS),
  },
  {
    label: 'All three statuses actually appear, so the later exercises have something to group',
    range: `${AP_TAB_NAME}!E2:E200`,
    assert: (rows) => {
      const present = new Set((rows || []).map((r) => norm((r || [])[0])).filter(Boolean))
      const missing = STATUSES.filter((s) => !present.has(norm(s)))
      return {
        ok: missing.length === 0,
        expected: STATUSES.join(', '),
        actual: missing.length ? `nothing is ${missing.join(' or ')}` : 'all three present',
        hint: missing.length
          ? 'Ask for a mix — a register where every invoice has the same status has nothing to summarise.'
          : null,
      }
    },
  },
  {
    label: 'There is at least one duplicate to find later: same vendor, same amount, different invoice number',
    range: `${AP_TAB_NAME}!A2:F200`,
    assert: (rows) => {
      const seen = new Map()
      let found = null
      ;(rows || []).forEach((r) => {
        const invoice = norm((r || [])[0])
        const key = `${norm((r || [])[1])}|${toNumber((r || [])[3])}`
        if (!invoice || key === '|NaN') return
        const previous = seen.get(key)
        if (previous && previous !== invoice && !found) found = key.split('|')[0]
        if (!previous) seen.set(key, invoice)
      })
      return {
        ok: found !== null,
        expected: 'one planted duplicate pair',
        actual: found ? `found one against ${found}` : 'no two invoices share a vendor and an amount',
        hint: found ? null : 'Ask the script to plant one deliberately — a later module is about finding it.',
      }
    },
  },
]

/**
 * Module 20 — Prompting for Sheet Work.
 *
 * The trainee sorts their register, adds a band column and builds a summary.
 * None of that can be checked against known values now, so it is checked
 * against THEIR register: the bands must match their own amounts, and the
 * summary must add up to their own rows. Arguably the honest version of the
 * question all along.
 */

/** Every invoice carries the band its own amount earns. */
function bandsMatchAmounts(rows) {
  // Range D:G, so each row is [Amount, Status, Approver, Amount Band].
  const AMOUNT = 0
  const BAND = 3

  const data = (rows || []).filter((r) => String((r || [])[AMOUNT] ?? '').trim() !== '')

  if (data.length !== ROW_COUNT) {
    return {
      ok: false,
      expected: `${ROW_COUNT} invoices, each with a band`,
      actual: `${data.length} rows with an amount`,
      hint: 'Some invoices are missing, or the Amount column has moved out of column D.',
    }
  }

  const wrong = []
  data.forEach((r) => {
    const amount = toNumber(r[AMOUNT])
    const band = String(r[BAND] ?? '').trim()
    const want = bandFor(amount)
    const shown = Number.isFinite(amount) ? `$${money(amount)}` : `"${r[AMOUNT]}"`
    if (!band) wrong.push(`${shown} has no band`)
    else if (norm(band) !== norm(want)) wrong.push(`${shown} says "${band}", should be "${want}"`)
  })

  const shownProblems = wrong.slice(0, 3).join('; ')
  return {
    ok: wrong.length === 0,
    expected: 'every band matches its amount',
    actual: wrong.length
      ? `${shownProblems}${wrong.length > 3 ? `; and ${wrong.length - 3} more` : ''}`
      : 'every band matches its amount',
    hint: wrong.length
      ? 'Ask again with the three labels and their cut-offs spelled out — that is the whole lesson of this module.'
      : null,
  }
}

export const MODULE_20_CHECKS = [
  {
    label: `The spreadsheet now has a "${SUMMARY_TAB_NAME}" tab as well as "${AP_TAB_NAME}"`,
    sheets: true,
    assert: hasTabs([AP_TAB_NAME, SUMMARY_TAB_NAME]),
  },
  {
    label: `Row 1 has the original six headings plus "${BAND_COLUMN_HEADER}"`,
    range: `${AP_TAB_NAME}!A1:G1`,
    assert: exactRow([...AP_HEADERS, BAND_COLUMN_HEADER]),
  },
  {
    label: `All ${ROW_COUNT} invoices survived the rework`,
    range: `${AP_TAB_NAME}!A2:A200`,
    assert: rowCount(ROW_COUNT),
  },
  {
    label: 'The invoices are sorted by Amount, largest first',
    range: `${AP_TAB_NAME}!D2:D200`,
    assert: columnDescending(),
  },
  {
    label: 'Every invoice is banded, and banded correctly',
    range: `${AP_TAB_NAME}!D2:G200`,
    assert: bandsMatchAmounts,
  },
  {
    label: `The ${SUMMARY_TAB_NAME} tab is headed ${SUMMARY_HEADERS.join(' and ')}`,
    range: `${SUMMARY_TAB_NAME}!A1:B1`,
    assert: exactRow(SUMMARY_HEADERS),
  },
  {
    label: `The ${SUMMARY_TAB_NAME} totals add up to what the register actually says`,
    // Two ranges: every invoice's amount and status (columns D and E, in that
    // order on the sheet), and the summary built from them. The totals are
    // recomputed from the trainee's own rows, because there is no longer a
    // right answer known in advance.
    ranges: [`${AP_TAB_NAME}!D2:E200`, `${SUMMARY_TAB_NAME}!A2:B20`],
    assert: summaryMatchesSource({ keyIndex: 1, valueIndex: 0 }),
  },
]
